import type { AnsweredQuestion } from "@sveabank/cc-common";
import { CLC_BASE_PATH } from "..";
import type {
	ClcAddonConfig,
	ClcBeneficialOwnerAnswerSet,
	ClcBeneficialOwnership,
	ClcCompanyRegistrationForm,
	ClcCompanySharesForm,
	ClcCompanyStructure,
	ClcDebitCardConfigForm,
	ClcPaymentConfig,
} from "../../types/clc-application";
import type { SveaRequest } from "../../types/request";
import { APPCONFIG } from "../../utils/config";

export const fetchClcApplications: SveaRequest = () => ({
	url: `${CLC_BASE_PATH}/application`,
	method: "GET",
});

export const initClcApplication: SveaRequest = (applicationId: string) => ({
	url: `${CLC_BASE_PATH}/application`,
	method: "POST",
	body: {
		applicationId,
	},
});

export const deleteClcApplication: SveaRequest = (applicationId: string) => ({
	url: `${CLC_BASE_PATH}/application/${applicationId}`,
	method: "DELETE",
});

export const postClcCompanyRegistration: SveaRequest = (
	applicationId: string,
	data: ClcCompanyRegistrationForm,
) => ({
	url: `${CLC_BASE_PATH}/application/${applicationId}/company-registration`,
	method: "POST",
	body: {
		...data,
	},
});

export const postClcCompanyShares: SveaRequest = (
	applicationId: string,
	data: ClcCompanySharesForm,
) => ({
	url: `${CLC_BASE_PATH}/application/${applicationId}/company-shares`,
	method: "POST",
	body: {
		...data,
	},
});

export const postClcBeneficialOwnership: SveaRequest = (
	applicationId: string,
	data: ClcBeneficialOwnership,
) => ({
	url: `${CLC_BASE_PATH}/application/${applicationId}/beneficial-owners`,
	method: "POST",
	body: {
		...data,
	},
});

export const postClcRepresentatives: SveaRequest = (
	applicationId: string,
	data: ClcCompanyStructure,
) => ({
	url: `${CLC_BASE_PATH}/application/${applicationId}/representatives`,
	method: "POST",
	body: {
		...data,
	},
});

export const fetchClcApplicationRevision: SveaRequest = (applicationId: string) => ({
	url: `${CLC_BASE_PATH}/application/${applicationId}`,
	method: "HEAD",
});

export const fetchClcApplicationDetails: SveaRequest = (applicationId: string) => ({
	url: `${CLC_BASE_PATH}/application/${applicationId}`,
	method: "GET",
});

export const fetchMunicipalities: SveaRequest = () => ({
	url: `${CLC_BASE_PATH}/municipalities`,
	method: "GET",
});

export const fetchClcSniCodes: SveaRequest = () => ({
	url: `${CLC_BASE_PATH}/sni-codes`,
	method: "GET",
});

export const fetchClcCountries: SveaRequest = () => ({
	url: `${CLC_BASE_PATH}/countries`,
	method: "GET",
});

export const fetchClcPersonData: SveaRequest = (applicationId: string, nationalId: string) => ({
	url: `${CLC_BASE_PATH}/application/${applicationId}/person-data`,
	method: "POST",
	body: {
		nationalId,
	},
});

export const fetchClcBeneficialOwnerQuestions: SveaRequest = (applicationId: string) => ({
	url: `${CLC_BASE_PATH}/application/${applicationId}/kyc-beneficial-owners-questions`,
	method: "GET",
});

export const fetchClcControlTypes: SveaRequest = () => ({
	url: `${CLC_BASE_PATH}/control-types`,
	method: "GET",
});

export const fetchClcAmlQuestions: SveaRequest = (applicationId: string) => ({
	url: `${CLC_BASE_PATH}/application/${applicationId}/aml-questions`,
	method: "GET",
});

export const postClcAmlAnswers: SveaRequest = (
	applicationId: string,
	productId: string,
	questions: Array<AnsweredQuestion>,
) => ({
	url: `${CLC_BASE_PATH}/application/${applicationId}/aml-answers`,
	method: "POST",
	body: {
		productId,
		questions,
	},
});

export const postClcBeneficialOwnerAnswers: SveaRequest = (
	applicationId: string,
	nationalId: string,
	questions: Array<AnsweredQuestion>,
	additional: Array<ClcBeneficialOwnerAnswerSet> | null,
) => ({
	url: `${CLC_BASE_PATH}/application/${applicationId}/kyc-beneficial-owners-answers`,
	method: "POST",
	body: {
		nationalId,
		questions,
		additional,
	},
});

export const getAvailableClcPackages: SveaRequest = (applicationId: string) => ({
	url: `${CLC_BASE_PATH}/application/${applicationId}/packages`,
	method: "GET",
});

export const setupClcAgreement: SveaRequest = (applicationId: string) => ({
	url: `${CLC_BASE_PATH}/application/${applicationId}/package/setup-agreement`,
	method: "POST",
});

export const fetchClcAgreement: SveaRequest = (applicationId: string) => ({
	url: `${CLC_BASE_PATH}/application/${applicationId}/package/agreement`,
	method: "GET",
	headers: {
		"content-type": "application/json",
	},
});

export const initializeClcAgreementSigning: SveaRequest = (applicationId: string) => ({
	url: `${CLC_BASE_PATH}/application/${applicationId}/package/sign-agreement`,
	method: "POST",
	body: {
		mode: sessionStorage.getItem("bankIdMode") || APPCONFIG.BANKID_MODE,
		provider: "eid",
	},
});

export const fetchClcSigningStatus: SveaRequest = (applicationId: string) => ({
	url: `${CLC_BASE_PATH}/application/${applicationId}/sign/collect`,
	method: "POST",
});

// export const finalizeSigning: SveaRequest = (applicationId: string) => ({
// 	url: `${BASE_PATH}/application/${applicationId}/sign/finalize`,
// 	method: "POST",
// });

export const cancelClcSign: SveaRequest = (applicationId: string) => ({
	url: `${CLC_BASE_PATH}/application/${applicationId}/sign/cancel`,
	method: "POST",
	body: {
		provider: "eid",
	},
});

export const fetchClcRoles: SveaRequest = () => ({
	url: `${CLC_BASE_PATH}/roles`,
	method: "GET",
});

export const fetchClcSignatoryCombinations: SveaRequest = () => ({
	url: `${CLC_BASE_PATH}/signatory-combinations`,
	method: "GET",
});

export const postClcApplication: SveaRequest = (applicationId: string) => ({
	url: `${CLC_BASE_PATH}/application/${applicationId}/submit`,
	method: "POST",
});

export const configureClcCard: SveaRequest = (
	applicationId: string,
	cardConfig: ClcDebitCardConfigForm,
) => ({
	url: `${CLC_BASE_PATH}/application/${applicationId}/product/configure-mastercard`,
	body: cardConfig,
	method: "POST",
});

export const selectClcAddon: SveaRequest = (
	applicationId: string,
	addonConfig: ClcAddonConfig,
) => ({
	url: `${CLC_BASE_PATH}/application/${applicationId}/addon`,
	body: { addons: [addonConfig] },
	method: "POST",
});

export const confirmClcProductConfig: SveaRequest = (applicationId: string) => ({
	url: `${CLC_BASE_PATH}/application/${applicationId}/confirm-package`,
	method: "POST",
});

export const removeClcAddons: SveaRequest = (applicationId: string, addons: Array<string>) => ({
	url: `${CLC_BASE_PATH}/application/${applicationId}/addon`,
	body: { addons: addons },
	method: "DELETE",
});

export const setClcOrganisationNumber: SveaRequest = (applicationId: string, orgNr: string) => ({
	url: `${CLC_BASE_PATH}/application/${applicationId}/organisation-number`,
	body: { orgNr },
	method: "POST",
});

export const createClcPaymentOrder: SveaRequest = (
	applicationId: string,
	config: ClcPaymentConfig,
) => ({
	url: `${CLC_BASE_PATH}/application/${applicationId}/order`,
	method: "POST",
	body: config,
	headers: {
		//"X-ProviderMode": "PIE",
		"X-ProviderMode": "default",
	},
});

export const fetchClcCards: SveaRequest = (applicationId: string) => ({
	url: `${CLC_BASE_PATH}/application/${applicationId}/cards`,
	method: "GET",
});

export const fetchClcSelectedPackage: SveaRequest = (applicationId: string) => ({
	url: `${CLC_BASE_PATH}/application/${applicationId}/selected-package`,
	method: "GET",
});

export const fetchClcApplicationQuestions: SveaRequest = (applicationId: string) => ({
	url: `${CLC_BASE_PATH}/application/${applicationId}/kyc-application-questions`,
	method: "GET",
});

export const fetchClcPossibleOrganisations: SveaRequest = (applicationId: string) => ({
	url: `${CLC_BASE_PATH}/application/${applicationId}/possible-organisations`,
	method: "GET",
});

export const postClcApplicationQuestions: SveaRequest = (
	applicationId: string,
	questions: Array<AnsweredQuestion>,
) => ({
	url: `${CLC_BASE_PATH}/application/${applicationId}/kyc-application-answers`,
	method: "POST",
	body: {
		questions,
	},
});

export const selectClcPackage: SveaRequest = (applicationId: string, packageId: string) => ({
	url: `${CLC_BASE_PATH}/application/${applicationId}/package`,
	method: "POST",
	body: {
		packageId,
	},
});

export const unSelectClcPackage: SveaRequest = (applicationId: string) => ({
	url: `${CLC_BASE_PATH}/application/${applicationId}/package`,
	method: "DELETE",
});
